import {
  Product,
  ProductDetail,
  ProductDto,
  ProductInfoExpanded,
  ProductInfo,
  ProductNote,
  ProductServicePack,
  PublishingStatus
} from '../../models/product';
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {OperatingSystem} from '../../models/operating-system';
import {catchError, map} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {ATTRIBUTE_DEF_ID, OS_CMP_CATEGORY, OsComponent} from '../../models/os-component';
import {AcmHttpResponse} from '../../models/acm-http';
import {AdaptMessageService} from '@bmc-ux/adapt-angular';
import {ProductHistory} from 'src/app/models/history';

export enum PRODUCT_PATH {
  PRODUCT = 'product',
  PRODUCT_VERSION = 'productversion'
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private productVersionUrl: string = PRODUCT_PATH.PRODUCT;

  constructor(private httpClient: HttpClient,
              private messageService: AdaptMessageService) {
  }

  public setProductVersionUrl(productVersionUrl: PRODUCT_PATH.PRODUCT | PRODUCT_PATH.PRODUCT_VERSION): void {
    this.productVersionUrl = productVersionUrl;
  }

  public createProductVersion(prodId: number): Observable<AcmHttpResponse> {
    return this.httpClient.post(
      `${environment.apiUrl}productversion/${prodId}`,
      null,
      {
        observe: 'response'
      })
      .pipe(
        catchError((err) => of(err)),
        map((response: HttpResponse<{}>) => ({statusCode: response.status}))
      ) as Observable<AcmHttpResponse>;
  }

  public updateProductVersion(prodId: number, fieldProductDto: Partial<ProductDto>): Observable<AcmHttpResponse> {
    return this.httpClient.put(
      `${environment.apiUrl}productversion/${prodId}`,
      fieldProductDto,
      {
        observe: 'response'
      })
      .pipe(
        map((response: HttpResponse<{}>) => ({statusCode: response.status}))
      ) as Observable<AcmHttpResponse>;
  }

  public deleteProductVersion(prodId: number): Observable<AcmHttpResponse> {
    return this.httpClient.request('DELETE', `${environment.apiUrl}productversion/${prodId}`,
      {
        observe: 'response'
      }
    ).pipe(
      catchError((err) => of(err)),
      map((response: HttpResponse<{}>) => ({statusCode: response.status}))
    ) as Observable<AcmHttpResponse>;
  }

  public updatePublishingStatus(
    prodId: number,
    newPublishingStatus: PublishingStatus.DRAFT | PublishingStatus.PUBLISHED | PublishingStatus.WITHDRAWN | PublishingStatus.REVIEW
  ): Observable<AcmHttpResponse> {
    let newPublishingStatusUrl: string;
    switch (newPublishingStatus) {
      case PublishingStatus.REVIEW:
        newPublishingStatusUrl = `${environment.apiUrl}productversion/${prodId}/pstatus/review`;
        break;
      case PublishingStatus.DRAFT:
        newPublishingStatusUrl = `${environment.apiUrl}productversion/${prodId}/pstatus/draft`;
        break;
      case PublishingStatus.PUBLISHED:
        newPublishingStatusUrl = `${environment.apiUrl}productversion/${prodId}/publish`;
        break;
      case PublishingStatus.WITHDRAWN:
        newPublishingStatusUrl = `${environment.apiUrl}product/${prodId}/pstatus/withdrawn`;
        break;
    }
    return this.httpClient.post(
      newPublishingStatusUrl,
      null,
      {
        observe: 'response'
      })
      .pipe(
        catchError((err) => of(err)),
        map((response: HttpResponse<{}>) => {
          return ({statusCode: response.status});
        })
      ) as Observable<AcmHttpResponse>;
  }

  public getProducts(productTypesParams: string): Observable<ProductInfo[]> {
    return this.httpClient.get(`${environment.apiUrl}products?productTypes=` + productTypesParams || '') as Observable<ProductInfo[]>;
  }

  public getProductInfo(smartNumber: string): Observable<ProductInfoExpanded> {
    return this.httpClient.get(`${environment.apiUrl}product/admin/${smartNumber}/infobysn`).pipe(
      catchError(() => of(null)),
      map((product: ProductInfoExpanded): ProductInfoExpanded => {
        const servicePacksValues: string[] = product ? Object.values(product.spSmartNumber2Name) : [];
        if (servicePacksValues.length) {
          const sortedServicePacks = this.createNewSortedSpId2Name(product.spSmartNumber2Name);
          return {
            ...product,
            servicePacks: sortedServicePacks
          };
        }
        return product;
      })
    ) as Observable<ProductInfoExpanded>;
  }

  public getProductVersionInfo(smartNumber: string): Observable<ProductInfoExpanded> {
    return this.httpClient.get(`${environment.apiUrl}productversion/${smartNumber}/infobysn`).pipe(
      catchError(() => of(null)),
      map((product: ProductInfoExpanded): ProductInfoExpanded => {
        const servicePacksValues: string[] = product ? Object.values(product.spSmartNumber2Name) : [];
        if (servicePacksValues.length) {
          const sortedServicePacks = this.createNewSortedSpId2Name(product.spSmartNumber2Name);
          return {
            ...product,
            servicePacks: sortedServicePacks
          };
        }
        return product;
      })
    ) as Observable<ProductInfoExpanded>;
  }

  public getProductNotes(id: number): Observable<ProductNote[]> {
    return this.httpClient.get(`${environment.apiUrl}${this.productVersionUrl}/${id}/notes`) as Observable<ProductNote[]>;
  }

  public createProductNotes(id: string, productNotes: ProductNote[]): Observable<{ statusCode: number }> {
    return this.httpClient.post(`${environment.apiUrl}productversion/${id}/notes`, productNotes,
      {observe: 'response'}).pipe(
      map((response: HttpResponse<ProductNote[]>) => ({statusCode: response.status}))
    ) as Observable<{ statusCode: number }>;
  }

  public updateProductNotes(id: string, productNotes: ProductNote[]): Observable<{ statusCode: number }> {
    return this.httpClient.put(`${environment.apiUrl}productversion/${id}/notes`, productNotes,
      {observe: 'response'}).pipe(
      map((response: HttpResponse<ProductNote[]>) => ({statusCode: response.status}))
    ) as Observable<{ statusCode: number }>;
  }

  public deleteProductNotes(id: string, productNoteIDs: number[]): Observable<{ statusCode: number }> {
    return this.httpClient.request('DELETE', `${environment.apiUrl}productversion/${id}/notes`,
      {
        body: productNoteIDs,
        observe: 'response'
      }).pipe(
      map((response: HttpResponse<ProductNote[]>) => ({statusCode: response.status}))
    ) as Observable<{ statusCode: number }>;
  }

  public getProductDetails(id: number, url: string = this.productVersionUrl): Observable<ProductDetail[]> {
    return this.httpClient.get(`${environment.apiUrl}${url}/${id}/details`)
      .pipe(
        map((productDetails: ProductDetail[]): ProductDetail[] => {
          productDetails.map((productDetail: ProductDetail): ProductDetail => {
            productDetail.operatingSystemDto.prodInstId = productDetail.prodInstId;
            productDetail.components = productDetail.components || [];
            return productDetail;
          });
          return productDetails as ProductDetail[];
        }),
      ) as Observable<ProductDetail[]>;
  }

  public getChildProducts(id: number): Observable<Product[]> {
    return this.httpClient.get(`${environment.apiUrl}${this.productVersionUrl}/${id}/childproducts`) as Observable<Product[]>;
  }

  public addChildProducts(prodId: number, childIds: number[]): Observable<AcmHttpResponse> {
    return this.httpClient.post(
      `${environment.apiUrl}productversion/${prodId}/childproducts`,
      childIds,
      {observe: 'response'})
      .pipe(
        map((response: HttpResponse<{}>): AcmHttpResponse => ({statusCode: response.status}))
      ) as Observable<AcmHttpResponse>;
  }

  public deleteChildProducts(prodId: number, childIds: number[]): Observable<AcmHttpResponse> {
    return this.httpClient.request(
      'DELETE',
      `${environment.apiUrl}productversion/${prodId}/childproducts`,
      {
        body: childIds,
        observe: 'response'
      }
    )
      .pipe(
        map((response: HttpResponse<{}>): AcmHttpResponse => ({statusCode: response.status}))
      ) as Observable<AcmHttpResponse>;
  }

  public getPrerequisites(id: number): Observable<Product[]> {
    return this.httpClient.get(`${environment.apiUrl}${this.productVersionUrl}/${id}/prereq`) as Observable<Product[]>;
  }

  public addPrerequisites(id: number, prereqIDs: number[]): Observable<AcmHttpResponse> {
    return this.httpClient.post(`${environment.apiUrl}${this.productVersionUrl}/${id}/prereq`, prereqIDs, {observe: 'response'})
      .pipe(
        map((response: HttpResponse<{}>) => ({statusCode: response.status}))
      ) as Observable<AcmHttpResponse>;
  }

  public deletePrerequisites(id: number, prereqIDs: number[]): Observable<AcmHttpResponse> {
    return this.httpClient.request('DELETE', `${environment.apiUrl}productversion/${id}/prereq`,
      {
        body: prereqIDs,
        observe: 'response'
      }
    ).pipe(
      map((response: HttpResponse<{}>) => ({statusCode: response.status}))
    ) as Observable<AcmHttpResponse>;
  }

  public getCompatibles(id: number): Observable<Product[]> {
    return this.httpClient.get(`${environment.apiUrl}${this.productVersionUrl}/${id}/compatibles`) as Observable<Product[]>;
  }

  public getCompatibleSuitesOrSolutions(id: number): Observable<Product[]> {
    return this.httpClient.get(`${environment.apiUrl}product/${id}/parentSuites `) as Observable<Product[]>;
  }

  public addCompatibles(id: number, compatibleIDs: number[]): Observable<AcmHttpResponse> {
    return this.httpClient.post(`${environment.apiUrl}${this.productVersionUrl}/${id}/compatibles`, compatibleIDs, {observe: 'response'})
      .pipe(
        map((response: HttpResponse<{}>) => ({statusCode: response.status}))
      ) as Observable<AcmHttpResponse>;
  }

  public deleteCompatibles(id: number, compatibleIDs: number[]): Observable<AcmHttpResponse> {
    return this.httpClient.request('DELETE', `${environment.apiUrl}productversion/${id}/compatibles`,
      {
        body: compatibleIDs,
        observe: 'response'
      }
    )
      .pipe(
        map((response: HttpResponse<{}>) => ({statusCode: response.status}))
      ) as Observable<AcmHttpResponse>;
  }

  public deleteCompatibleOs(prodId: number, osIds: number[], isComponentMasterRecord: boolean, errorMessage: string): Observable<AcmHttpResponse> {
    const productPath = isComponentMasterRecord ? PRODUCT_PATH.PRODUCT : PRODUCT_PATH.PRODUCT_VERSION;
    return this.httpClient.request(
      'DELETE',
      `${environment.apiUrl}${productPath}/${prodId}/os`,
      {
        body: osIds,
        observe: 'response'
      })
      .pipe(
        catchError((err: HttpErrorResponse): Observable<null> => {
          return this.handleErrors(err, errorMessage);
        }),
        map((response: HttpResponse<{}>): AcmHttpResponse => {
          return response ? {statusCode: response.status} : null;
        })
      ) as Observable<AcmHttpResponse>;
  }

  public addCompatibleOs(prodId: number, newOperatingSystems: OperatingSystem[], isComponentMasterRecord: boolean): Observable<number[]> {
    const productPath = isComponentMasterRecord ? PRODUCT_PATH.PRODUCT : PRODUCT_PATH.PRODUCT_VERSION;
    return this.httpClient.post(`${environment.apiUrl}${productPath}/${prodId}/os`, newOperatingSystems)
      .pipe(
        catchError((err: HttpErrorResponse): Observable<null> => {
          const errorMessage = 'Something went wrong, operating systems has not been added, please, try again later.';
          return this.handleErrors(err, errorMessage);
        }),
      ) as Observable<number[]>;
  }

  public addComponentsToProductOs(prodId: number, osId: number, osComponents: OsComponent[], isComponentMasterRecord: boolean): Observable<AcmHttpResponse> {
    const productPath = isComponentMasterRecord ? PRODUCT_PATH.PRODUCT : PRODUCT_PATH.PRODUCT_VERSION;
    const newComponents = osComponents.map((curOsComponent: OsComponent) => ({
      ...curOsComponent,
      cmpCategory: curOsComponent.cmpCategory === OS_CMP_CATEGORY.TPC_PREREQ ? 'TPC-PREREQ' : curOsComponent.cmpCategory
    }));

    return this.httpClient.post(
      `${environment.apiUrl}${productPath}/${prodId}/os/${osId}/components`, newComponents, {observe: 'response'}
    ).pipe(
      catchError((err) => of(err)),
      map((res: HttpResponse<{}>): AcmHttpResponse => ({statusCode: res.status}))
    ) as Observable<AcmHttpResponse>;
  }

  public updateProductOsAttribute(
    prodId: number, osId: number, attributeDefId: ATTRIBUTE_DEF_ID.NOTES, attributeValue: string, isComponentMasterRecord: boolean
  ): Observable<AcmHttpResponse> {
    const productPath = isComponentMasterRecord ? PRODUCT_PATH.PRODUCT : PRODUCT_PATH.PRODUCT_VERSION;
    return this.httpClient.put(
      `${environment.apiUrl}${productPath}/${prodId}/os/${osId}/attribute/${attributeDefId}`,
      attributeValue,
      {
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<{}>): AcmHttpResponse => ({
          statusCode: res.status
        })),
        catchError(() => of(null))
      ) as Observable<AcmHttpResponse>;
  }

  public deleteProductOsAttribute(prodId: number, osId: number, attributeDefId: ATTRIBUTE_DEF_ID.NOTES, isComponentMasterRecord: boolean): Observable<AcmHttpResponse> {
    const productPath = isComponentMasterRecord ? PRODUCT_PATH.PRODUCT : PRODUCT_PATH.PRODUCT_VERSION;
    return this.httpClient.request(
      'DELETE',
      `${environment.apiUrl}${productPath}/${prodId}/os/${osId}/attribute/${attributeDefId}`,
      {
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<{}>): AcmHttpResponse => ({
          statusCode: res.status
        })),
        catchError(() => of(null))
      ) as Observable<AcmHttpResponse>;
  }

  public deleteComponentsOfProductOs(prodId: number, osId: number, compIds: number[], isComponentMasterRecord: boolean): Observable<AcmHttpResponse> {
    const productPath = isComponentMasterRecord ? PRODUCT_PATH.PRODUCT : PRODUCT_PATH.PRODUCT_VERSION;
    return this.httpClient.request(
      'DELETE',
      `${environment.apiUrl}${productPath}/${prodId}/os/${osId}/components`,
      {
        body: compIds,
        observe: 'response'
      }
    )
      .pipe(
        map((res: HttpResponse<{}>): AcmHttpResponse => ({
          statusCode: res.status
        })),
        catchError(() => of(null))
      ) as Observable<AcmHttpResponse>;
  }

  public addUiLanguageToOs(prodId: number, osId: number, languageIds: number[], isComponentMasterRecord: boolean): Observable<AcmHttpResponse> {
    const productPath = isComponentMasterRecord ? PRODUCT_PATH.PRODUCT : PRODUCT_PATH.PRODUCT_VERSION;
    return this.httpClient.post(
      `${environment.apiUrl}${productPath}/${prodId}/os/${osId}/uilanguages`,
      languageIds,
      {observe: 'response'})
      .pipe(
        map((res: HttpResponse<{}>): AcmHttpResponse => ({
          statusCode: res.status
        })),
        catchError(() => of(null))
      ) as Observable<AcmHttpResponse>;
  }

  public deleteUiLanguageOfProductOs(prodId: number, osId: number, languageIds: number[], isComponentMasterRecord: boolean): Observable<AcmHttpResponse> {
    const productPath = isComponentMasterRecord ? PRODUCT_PATH.PRODUCT : PRODUCT_PATH.PRODUCT_VERSION;
    return this.httpClient.request(
      'DELETE',
      `${environment.apiUrl}${productPath}/${prodId}/os/${osId}/uilanguages`,
      {
        body: languageIds,
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<{}>): AcmHttpResponse => ({
          statusCode: res.status
        })),
        catchError(() => of(null))
      ) as Observable<AcmHttpResponse>;
  }

  public addDocsLanguageToOs(prodId: number, osId: number, languageIds: number[], isComponentMasterRecord: boolean): Observable<AcmHttpResponse> {
    const productPath = isComponentMasterRecord ? PRODUCT_PATH.PRODUCT : PRODUCT_PATH.PRODUCT_VERSION;
    return this.httpClient.post(
      `${environment.apiUrl}${productPath}/${prodId}/os/${osId}/doclanguages`,
      languageIds,
      {observe: 'response'})
      .pipe(
        map((res: HttpResponse<{}>): AcmHttpResponse => ({
          statusCode: res.status
        })),
        catchError(() => of(null))
      ) as Observable<AcmHttpResponse>;
  }

  public deleteDocsLanguageOfProductOs(prodId: number, osId: number, languageIds: number[], isComponentMasterRecord: boolean): Observable<AcmHttpResponse> {
    const productPath = isComponentMasterRecord ? PRODUCT_PATH.PRODUCT : PRODUCT_PATH.PRODUCT_VERSION;
    return this.httpClient.request(
      'DELETE',
      `${environment.apiUrl}${productPath}/${prodId}/os/${osId}/doclanguages`,
      {
        body: languageIds,
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<{}>): AcmHttpResponse => ({
          statusCode: res.status
        })),
        catchError(() => of(null))
      ) as Observable<AcmHttpResponse>;
  }

  public getProductEditHistoryBySN(smartNumber: string): Observable<ProductHistory[]> {
    return this.httpClient.get(`${environment.apiUrl}item/product/edithistory`, {
      params: {
        smartNumber: smartNumber,
      },
    }).pipe(
      catchError(() => of(null))
    ) as Observable<ProductHistory[]>;
  }

  private createNewSortedSpId2Name(curServicePack: { [smartNumber: string]: string }): ProductServicePack[] {
    const sortedServicePackNames: string[] = Object.values(curServicePack).sort();
    const newSortedSpId2Name: ProductServicePack[] = sortedServicePackNames
      .map((curSpName: string): ProductServicePack => {
        const curSpId: string = Object.keys(curServicePack)
          .find((currentSpId: string): boolean => curServicePack[currentSpId] === curSpName);
        return {
          smartNumber: curSpId,
          name: curSpName
        };
      });
    return newSortedSpId2Name;
  }

  private handleErrors(err: HttpErrorResponse, errorMessage: string): Observable<null> {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage
    });
    console.error(err);
    return of(null);
  }
}
